// src/components/credits/AddFunds.js
//
// The Add Funds modal, opened from every place a purchase is refused for
// funds (mission 005). One provider (in App) renders one modal:
//
//   const { openAddFunds } = useAddFunds();
//   catch (err) { if (FUNDS_CODES.has(err.code)) openAddFunds({ error: err, required_cents: price, context: 'room_ticket', onFunded: refresh }); }
//   <AddFundsButton error={err} required_cents={price} context="gift" onFunded={refresh} />
//
// The modal shows the missing amount from the shortfall contract
// (lib/addFunds.js: required / available / shortfall, one subtraction) and
// the shared AddFundsPanel. onFunded fires once, for a credited order of the
// same account and network, and only while the caller is still mounted. It
// REFRESHES only: callers re-read / re-quote, and a purchase always needs
// another explicit confirmation (nothing is bought automatically).
//
// The welcome offer (mission 008, decisions 008-01 §5b and 008-02 §4): opens
// WelcomeOfferModal once per browser session (sessionStorage
// lunexx.welcomeOfferShown), only while summary.promo is eligible and the
// session can sign a deposit, when
//   (a) the modal is cancelled (X, backdrop, Escape) with no order created in
//       it, or
//   (b) a new member finished the profile nudge (window event
//       lunexx:new-member, fired by WalletContext; openWelcomeOffer() on the
//       context does the same).
// Its claim opens this modal — for the same request in (a) — with the pay
// amount prefilled.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Wallet, X } from 'lucide-react';
import ModalShell from '../ui/modal-shell';
import AddFundsPanel from './AddFundsPanel';
import WelcomeOfferModal from './WelcomeOfferModal';
import { addFundsSession, fundsCopy, shortfall } from '../../lib/addFunds';
import { useBalanceSummary } from '../../hooks/useBalanceSummary';
import { welcomeCards } from '../../lib/addFunds';
import { useWallet } from '../../context/WalletContext';

const Ctx = createContext(null);

const OFFER_KEY = 'lunexx.welcomeOfferShown';
/** shown this browser session; a browser that can't record it never sees it (it would reopen every time) */
const offerAllowed = () => {
  try {
    if (window.sessionStorage.getItem(OFFER_KEY) === '1') return false;
    window.sessionStorage.setItem(OFFER_KEY, '1');
    return window.sessionStorage.getItem(OFFER_KEY) === '1';
  } catch { return false; }
};

function AddFundsModal({ request, onClose }) {
  const { walletAddress } = useWallet();
  const { summary } = useBalanceSummary();
  const [funded, setFunded] = useState(false);
  const ordered = useRef(false);   // an order was created (or reported) while this modal was open
  const session = useRef(null);
  const account = walletAddress ? walletAddress.toLowerCase() : null;
  const sessionAccount = useRef(account);
  // the account changed while open: this session's callbacks are no longer this caller's
  if (session.current && sessionAccount.current !== account) { session.current.dispose(); session.current = null; }
  sessionAccount.current = account;
  // closing the modal disposes the session: a late "credited" never calls back (01d §2)
  useEffect(() => () => { session.current?.dispose(); }, []);
  if (!session.current && summary?.network_id) {
    session.current = addFundsSession({
      account: walletAddress, networkId: summary.network_id, context: request.context,
      onFunded: (x) => { setFunded(true); request.onFunded?.(x); },
    });
    request.register?.(session.current);
  }
  const s = shortfall({ error: request.error, required_cents: request.required_cents, summary });
  const copy = fundsCopy({ ...s, code: request.error?.code, frozen: !!summary?.frozen });
  const close = () => onClose({ ordered: ordered.current, summary });
  return (
    <ModalShell open onClose={close} labelledBy="add-funds-title" size="md">
      <div className="flex items-center justify-between border-b border-white/[0.06] px-6 py-4">
        <h2 id="add-funds-title" className="text-[17px] font-bold">Add funds</h2>
        <button type="button" onClick={close} aria-label="Close"
          className="flex h-11 w-11 items-center justify-center rounded-full text-zinc-500 hover:bg-white/[0.05] hover:text-white"><X size={18} /></button>
      </div>
      <div className="flex flex-col gap-4 p-6">
        <p className="text-[15px] leading-relaxed text-zinc-300" role="status">
          {funded
            ? 'Funds added. Go back and try again: the price is checked again and nothing is bought until you confirm. If the balance was spent in another tab, you may need to add more.'
            : copy}
        </p>
        <AddFundsPanel compact initialAmountCents={request.initialAmountCents ?? null}
          onOrderPhase={(phase, meta) => { ordered.current = true; session.current?.orderPhase(phase, meta); }} />
      </div>
    </ModalShell>
  );
}

const offerFor = (summary) => (summary?.promo?.eligible === true && welcomeCards(summary.promo).length > 0 ? summary.promo : null);

export function AddFundsProvider({ children }) {
  const { canSign } = useWallet();
  const { refresh } = useBalanceSummary({ enabled: false });
  const [request, setRequest] = useState(null);
  const [offer, setOffer] = useState(null);   // { request, promo }
  const live = useRef({ request: null, offer: null, canSign, refresh });
  live.current = { request, offer, canSign, refresh };
  const openAddFunds = useCallback((req = {}) => { setOffer(null); setRequest({ ...req, key: Date.now() }); }, []);
  const close = useCallback(({ ordered = false, summary = null } = {}) => {
    const req = request;
    setRequest(null);
    // a cancel with nothing started, by an account the offer is for, once per session
    if (!req || req.fromWelcomeOffer || ordered || canSign !== true) return;
    const promo = offerFor(summary);
    if (!promo || !offerAllowed()) return;
    setOffer({ request: req, promo });
  }, [request, canSign]);
  /** the sign-up trigger: a fresh read; never over an open modal */
  const openWelcomeOffer = useCallback(async () => {
    if (live.current.request || live.current.offer || live.current.canSign !== true) return;
    let summary = null;
    try { summary = await live.current.refresh(); } catch { return; }
    const promo = offerFor(summary);
    if (!promo || live.current.request || live.current.offer || live.current.canSign !== true || !offerAllowed()) return;
    setOffer({ request: null, promo });
  }, []);
  useEffect(() => {
    const f = () => { openWelcomeOffer(); };
    window.addEventListener('lunexx:new-member', f);
    return () => window.removeEventListener('lunexx:new-member', f);
  }, [openWelcomeOffer]);
  const accept = useCallback((cents) => {
    const req = offer?.request ?? { context: 'welcome_offer' };
    setOffer(null);
    setRequest({ ...req, initialAmountCents: cents, fromWelcomeOffer: true, key: Date.now() });
  }, [offer]);
  const value = useMemo(() => ({ openAddFunds, openWelcomeOffer }), [openAddFunds, openWelcomeOffer]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {request && <AddFundsModal key={request.key} request={request} onClose={close} />}
      {offer && !request && (
        <WelcomeOfferModal promo={offer.promo} onAccept={accept} onDismiss={() => setOffer(null)} />
      )}
    </Ctx.Provider>
  );
}

/** openAddFunds({ error, required_cents, context, onFunded }); sessions opened
 *  here stop calling back once the calling component unmounts */
export function useAddFunds() {
  const ctx = useContext(Ctx);
  const sessions = useRef(new Set());
  // the caller's lifetime, tracked independently of its sessions (decision 04 §3): a session that
  // registers after the caller unmounted (the modal's summary arrived late) is disposed at once
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; sessions.current.forEach((s) => s.callerUnmounted()); };
  }, []);
  const openAddFunds = useCallback((req = {}) => {
    ctx?.openAddFunds({ ...req, register: (s) => { if (alive.current) sessions.current.add(s); else s.dispose(); } });
  }, [ctx]);
  return { openAddFunds, available: !!ctx };
}

const VARIANTS = {
  // the constitution's white primary, at a 44px target
  primary: 'inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-[14px] bg-white px-4 text-[14px] font-bold text-black shadow-[0_12px_35px_rgba(255,255,255,0.08)] transition hover:bg-zinc-200 active:scale-[0.98]',
  // inside a quiet line of text (the chat meter): a link, still a 44px target
  link: 'inline-flex min-h-[44px] items-center text-zinc-300 underline-offset-2 hover:underline',
};

/** className adds LAYOUT only (margins, alignment); the look is the variant's */
export function AddFundsButton({ error = null, required_cents = null, context = null, onFunded = null, className = '', label = 'Add funds', icon = false, variant = 'primary' }) {
  const { openAddFunds } = useAddFunds();
  return (
    <button type="button" onClick={() => openAddFunds({ error, required_cents, context, onFunded })}
      className={`${VARIANTS[variant] ?? VARIANTS.primary} ${className}`}>
      {icon && <Wallet size={15} />} {label}
    </button>
  );
}
